/* A single row in the feed list. Shows thumbnail, title, source, time, and tags.
   Keyboard shortcuts (when focused): Space=open, x/ArrowLeft=delete, c=complete, q/ArrowRight=queue, b=backlog, n=note. */

import { memo, useEffect, useRef, useState, useCallback, type KeyboardEvent } from "react";
import { motion } from "framer-motion";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	Plus,
	X,
	Check,
	Archive,
	MoreVertical,
	StickyNote,
	Star,
	ShieldOff,
	Pin,
	PinOff,
	Sparkles,
	MessageCircle,
	ListVideo,
} from "lucide-react";
import { InlineCompletionCheck, InlineActionButton } from "@/components/feed/CompletionAnimation";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatTimeAgo } from "@/lib/formatTimeAgo";
import { formatDuration } from "@/lib/formatDuration";
import { ThumbnailPlaceholder } from "@/components/feed/ThumbnailPlaceholder";
import { useUserSettings } from "@/hooks/useUserSettings";
import { dispatchChatAboutItem, ANALYZABLE_CONTENT_TYPES } from "@/lib/chatAboutItem";
import { useFeedItemNote } from "@/hooks/useFeedItemNote";
import { NoteEditPopover } from "@/components/feed/NoteEditPopover";
import { PodcastPlayButton } from "@/components/player/PodcastPlayButton";
import { YouTubePlayButton } from "@/components/player/YouTubePlayButton";
import { ArticleReadButton } from "@/components/reader/ArticleReadButton";

export interface FeedItemData {
	_id: string;
	title: string;
	thumbnail?: string;
	sourceName?: string;
	sourceTags?: string[];
	contentType?: string;
	sourceStarred?: boolean;
	publishedAt: number;
	link: string;
	duration?: number;
	isProvocative?: boolean;
	note?: string;
	bookAuthor?: string;
	bookTotalPages?: number;
	bookCover?: string;
	bookPagesPerDay?: number;
	bookCurrentPage?: number;
	bookPageStart?: number;
	bookPageEnd?: number;
	movieImdbId?: string;
	movieDirector?: string;
	movieYear?: string;
	movieGenres?: string;
	movieRuntime?: string;
	movieRating?: string;
	moviePoster?: string;
	moviePlot?: string;
	movieActors?: string;
	sourceId?: string;
	userSubscriptionId?: string;
	audioUrl?: string;
	sourceSkipProvocationCheck?: boolean;
	forYouReason?: string;
	forYouReasonDetail?: string;
	aiTopics?: string[];
	playlistName?: string;
	playlistVideoPosition?: number;
	playlistTotalVideos?: number;
	tvEpisodeTitle?: string;
	tvSeason?: number;
	tvEpisode?: number;
	tvShowTitle?: string;
	tvShowPoster?: string;
	tvShowNetwork?: string;
}

export type PodcastBackdrop = "blur" | "vivid" | "dark-blur" | "stretch" | "gradient";

interface FeedItemRowProps {
	item: FeedItemData;
	hideBorder?: boolean;
	podcastBackdrop?: PodcastBackdrop;
	onOpen?: () => void;
	onDelete?: () => void;
	onComplete?: () => void;
	onQueue?: () => void;
	onBacklog?: () => void;
	onTagClick?: (tag: string) => void;
	onTopicClick?: (topic: string) => void;
	onToggleSkipProvocation?: () => void;
	onPin?: () => void;
	onUnpin?: () => void;
	onSourceClick?: (sourceId: string) => void;
}

function clearOtherActiveRows(current: HTMLElement) {
	document
		.querySelectorAll('[data-slot="feed-row"][data-active]')
		.forEach((el) => {
			if (el !== current) el.removeAttribute("data-active");
		});
}

export const FeedItemRow = memo(function FeedItemRow({
	item,
	hideBorder,
	podcastBackdrop = "vivid",
	onOpen,
	onDelete,
	onComplete,
	onQueue,
	onBacklog,
	onTagClick,
	onTopicClick,
	onToggleSkipProvocation,
	onPin,
	onUnpin,
	onSourceClick,
}: FeedItemRowProps) {
	const rowRef = useRef<HTMLDivElement>(null);
	const [isHovered, setIsHovered] = useState(false);
	const [isPendingAction, setIsPendingAction] = useState(false);
	const { animationsEnabled, feedItemSize, analyzeContent } = useUserSettings();
	const isCompact = feedItemSize === "compact";
	const noteState = useFeedItemNote(item._id, item.note);
	const hasMediaContent = item.contentType === "podcast" || item.contentType === "video";
	const mediaNotesCount = useQuery(
		api.mediaNotes.countByFeedItem,
		hasMediaContent ? { feedItemId: item._id as Id<"feedItems"> } : "skip",
	);

	const transition = animationsEnabled ? "transition-all duration-200" : "";

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (
				rowRef.current?.hasAttribute("data-active") &&
				!rowRef.current.contains(e.target as Node)
			) {
				rowRef.current.removeAttribute("data-active");
				rowRef.current.blur();
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	function handleFocus() {
		if (rowRef.current) {
			clearOtherActiveRows(rowRef.current);
			rowRef.current.setAttribute("data-active", "");
		}
	}

	// Deferred action: stored so it can fire after the exit animation completes
	const deferredActionRef = useRef<(() => void) | null>(null);

	function runAction(action?: () => void) {
		if (!action) return;
		setIsPendingAction(true);
		action();
	}

	// Animated action flow: button animation → exit slide → backend call
	const [isCompletingAnimated, setIsCompletingAnimated] = useState(false);
	const [isQueueAnimated, setIsQueueAnimated] = useState(false);
	const [isDismissAnimated, setIsDismissAnimated] = useState(false);
	const [isBacklogAnimated, setIsBacklogAnimated] = useState(false);
	const [isExitingAnimated, setIsExitingAnimated] = useState(false);

	const startAnimatedAction = useCallback((action: (() => void) | undefined, setter: (v: boolean) => void) => {
		if (!action) return;
		deferredActionRef.current = action;
		setter(true);
	}, []);

	const handleAnimatedComplete = useCallback(() => {
		if (!onComplete || isCompletingAnimated) return;
		deferredActionRef.current = onComplete;
		setIsCompletingAnimated(true);
	}, [onComplete, isCompletingAnimated]);

	// Called when any button animation finishes — starts the exit slide
	const handleActionAnimationDone = useCallback(() => {
		setIsExitingAnimated(true);
	}, []);

	function handleKeyDown(e: KeyboardEvent) {
		switch (e.key) {
			case " ":
				e.preventDefault();
				onOpen?.();
				break;
			case "x":
			case "ArrowLeft":
				e.preventDefault();
				if (animationsEnabled) {
					startAnimatedAction(onDelete, setIsDismissAnimated);
				} else {
					runAction(onDelete);
				}
				break;
			case "c":
				e.preventDefault();
				if (animationsEnabled) {
					handleAnimatedComplete();
				} else {
					runAction(onComplete);
				}
				break;
			case "q":
			case "ArrowRight":
				e.preventDefault();
				if (animationsEnabled) {
					startAnimatedAction(onQueue, setIsQueueAnimated);
				} else {
					runAction(onQueue);
				}
				break;
			case "b":
				e.preventDefault();
				if (animationsEnabled) {
					startAnimatedAction(onBacklog, setIsBacklogAnimated);
				} else {
					runAction(onBacklog);
				}
				break;
			case "n":
				e.preventDefault();
				noteState.startEditing();
				break;
			case "Escape":
				e.preventDefault();
				rowRef.current?.removeAttribute("data-active");
				rowRef.current?.blur();
				break;
		}
	}

	const thumbnailHeight = isCompact ? "h-[68px]" : "h-[108px]";
	const titleClass = isCompact
		? `text-sm font-medium leading-tight truncate max-w-full cursor-pointer hover:underline text-heading-foreground ${transition}`
		: `text-base font-medium leading-snug line-clamp-2 max-w-full cursor-pointer hover:underline text-heading-foreground ${transition}`;
	const metaTextSize = isCompact ? "text-xs" : "text-[13px]";

	const playlistBadge = item.playlistName ? (
		<span className="inline-flex items-center gap-1.5 text-[11px] bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 w-fit">
			<ListVideo className="size-3 shrink-0" />
			{item.playlistName}
		</span>
	) : null;

	const topicChips = item.aiTopics && item.aiTopics.length > 0 ? (
		<div className="flex items-center gap-1.5 flex-wrap">
			{item.aiTopics.map((topic) => (
				<button
					key={topic}
					type="button"
					className={`${metaTextSize} text-primary/70 hover:text-primary cursor-pointer transition-colors`}
					onClick={(e) => {
						e.stopPropagation();
						onTopicClick?.(topic);
					}}
					onMouseDown={(e) => e.stopPropagation()}
				>
					{topic}
				</button>
			))}
		</div>
	) : null;

	const actionButtons = (
		<TooltipProvider>
			<div className="flex items-center gap-1">
				<Tooltip>
					<TooltipTrigger asChild>
						{animationsEnabled ? (
							<div>
								<InlineActionButton
									type="queue"
									icon={<Plus className="size-3.5" />}
									onAnimationDone={() => { deferredActionRef.current = onQueue ?? null; handleActionAnimationDone(); }}
									triggerAnimation={isQueueAnimated}
								/>
							</div>
						) : (
							<Button
								variant="ghost"
								size="icon-sm"
								className="bg-muted active:scale-95"
								tabIndex={-1}
								onClick={() => runAction(onQueue)}
							>
								<Plus className="size-3.5" />
							</Button>
						)}
					</TooltipTrigger>
					<TooltipContent>
						<span>Queue</span>
						<kbd className="ml-1.5 text-[10px] opacity-60">Q</kbd>
						<span className="mx-0.5 text-[10px] opacity-40">or</span>
						<kbd className="text-[10px] opacity-60">&rarr;</kbd>
					</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						{animationsEnabled ? (
							<div>
								<InlineActionButton
									type="dismiss"
									icon={<X className="size-3.5" />}
									onAnimationDone={() => { deferredActionRef.current = onDelete ?? null; handleActionAnimationDone(); }}
									triggerAnimation={isDismissAnimated}
								/>
							</div>
						) : (
							<Button
								variant="ghost"
								size="icon-sm"
								className="bg-muted active:scale-95"
								tabIndex={-1}
								onClick={() => runAction(onDelete)}
							>
								<X className="size-3.5" />
							</Button>
						)}
					</TooltipTrigger>
					<TooltipContent>
						<span>Delete</span>
						<kbd className="ml-1.5 text-[10px] opacity-60">X</kbd>
						<span className="mx-0.5 text-[10px] opacity-40">or</span>
						<kbd className="text-[10px] opacity-60">&larr;</kbd>
					</TooltipContent>
				</Tooltip>
			</div>
			<div className="w-px h-8 bg-muted" />
			<div className="flex items-center gap-1">
				<Tooltip>
					<TooltipTrigger asChild>
						{animationsEnabled ? (
							<div>
								<InlineCompletionCheck
									variant="twitter-ring"
									onAnimationDone={() => { deferredActionRef.current = onComplete ?? null; handleActionAnimationDone(); }}
									triggerAnimation={isCompletingAnimated}
								/>
							</div>
						) : (
							<Button
								variant="ghost"
								size="icon-sm"
								className="bg-muted active:scale-95"
								tabIndex={-1}
								onClick={() => runAction(onComplete)}
							>
								<Check className="size-3.5" />
							</Button>
						)}
					</TooltipTrigger>
					<TooltipContent>
						<span>Complete</span>
						<kbd className="ml-1.5 text-[10px] opacity-60">C</kbd>
					</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						{animationsEnabled ? (
							<div>
								<InlineActionButton
									type="backlog"
									icon={<Archive className="size-3.5" />}
									onAnimationDone={() => { deferredActionRef.current = onBacklog ?? null; handleActionAnimationDone(); }}
									triggerAnimation={isBacklogAnimated}
								/>
							</div>
						) : (
							<Button
								variant="ghost"
								size="icon-sm"
								className="bg-muted active:scale-95"
								tabIndex={-1}
								onClick={() => runAction(onBacklog)}
							>
								<Archive className="size-3.5" />
							</Button>
						)}
					</TooltipTrigger>
					<TooltipContent>
						<span>Backlog</span>
						<kbd className="ml-1.5 text-[10px] opacity-60">B</kbd>
					</TooltipContent>
				</Tooltip>
				<Tooltip>
					<NoteEditPopover
						open={noteState.isEditing}
						onOpenChange={(open) =>
							open ? noteState.startEditing() : noteState.cancelEditing()
						}
						initialNote={noteState.displayNote}
						onSave={noteState.saveNote}
					>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon-sm"
								className="bg-muted active:scale-95"
								tabIndex={-1}
							>
								<StickyNote className="size-3.5" />
							</Button>
						</TooltipTrigger>
					</NoteEditPopover>
					<TooltipContent>
						<span>{noteState.hasNote ? "Edit note" : "Add note"}</span>
						<kbd className="ml-1.5 text-[10px] opacity-60">N</kbd>
					</TooltipContent>
				</Tooltip>
				<DropdownMenu>
					<Tooltip>
						<TooltipTrigger asChild>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon-sm"
									className="bg-muted active:scale-95"
									tabIndex={-1}
								>
									<MoreVertical className="size-3.5" />
								</Button>
							</DropdownMenuTrigger>
						</TooltipTrigger>
						<TooltipContent>More</TooltipContent>
					</Tooltip>
					<DropdownMenuContent align="end">
						{analyzeContent && item.contentType && ANALYZABLE_CONTENT_TYPES.includes(item.contentType) && (
							<DropdownMenuItem onClick={() => dispatchChatAboutItem(item._id, item.title, item.contentType!)}>
								<MessageCircle className="size-4 mr-2" />
								Chat about this
							</DropdownMenuItem>
						)}
						{onPin && (
							<DropdownMenuItem onClick={onPin}>
								<Pin className="size-4 mr-2" />
								Pin to top
							</DropdownMenuItem>
						)}
						{onUnpin && (
							<DropdownMenuItem onClick={onUnpin}>
								<PinOff className="size-4 mr-2" />
								Unpin
							</DropdownMenuItem>
						)}
						{item.sourceId && onToggleSkipProvocation && (
							<DropdownMenuItem onClick={onToggleSkipProvocation}>
								<ShieldOff className="size-4 mr-2" />
								{item.sourceSkipProvocationCheck
									? "Re-enable provocative filter for source"
									: "Exclude source from provocative filter"}
							</DropdownMenuItem>
						)}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</TooltipProvider>
	);

	const isPodcastWithAudio = item.contentType === "podcast" && item.audioUrl;
	const isPodcastCover = item.contentType === "podcast";
	const isYouTubeVideo = item.contentType === "video";
	const isArticle = item.contentType === "article";

	const thumbnail = (
		<div
			className={`shrink-0 ${thumbnailHeight} cursor-pointer ${transition} ${isPodcastCover ? `relative flex items-center justify-center overflow-hidden rounded-sm ${!item.thumbnail ? "bg-muted" : ""}` : ""}`}
			style={isPodcastCover
				? { width: isCompact ? 122 : 194 }
				: { aspectRatio: "122 / 68" }}
			onMouseDown={(e) => e.preventDefault()}
			onClick={() => onOpen?.()}
		>
			{isPodcastCover && item.thumbnail && podcastBackdrop === "blur" && (
				<img
					src={item.thumbnail}
					alt=""
					className="absolute inset-0 w-full h-full object-cover blur-2xl scale-150 opacity-40"
					aria-hidden="true"
				/>
			)}
			{isPodcastCover && item.thumbnail && podcastBackdrop === "vivid" && (
				<img
					src={item.thumbnail}
					alt=""
					className="absolute inset-0 w-full h-full object-cover blur-lg scale-150 opacity-80 saturate-200"
					aria-hidden="true"
				/>
			)}
			{isPodcastCover && item.thumbnail && podcastBackdrop === "dark-blur" && (
				<>
					<img
						src={item.thumbnail}
						alt=""
						className="absolute inset-0 w-full h-full object-cover blur-2xl scale-150 opacity-60"
						aria-hidden="true"
					/>
					<div className="absolute inset-0 bg-black/60" aria-hidden="true" />
				</>
			)}
			{isPodcastCover && item.thumbnail && podcastBackdrop === "stretch" && (
				<img
					src={item.thumbnail}
					alt=""
					className="absolute inset-0 w-full h-full object-cover opacity-30"
					aria-hidden="true"
				/>
			)}
			{isPodcastCover && item.thumbnail && podcastBackdrop === "gradient" && (
				<>
					<img
						src={item.thumbnail}
						alt=""
						className="absolute inset-0 w-full h-full object-cover scale-110 opacity-60"
						aria-hidden="true"
					/>
					<div
						className="absolute inset-0"
						style={{
							background: "radial-gradient(circle at center, transparent 30%, oklch(0.97 0 0 / 0.95) 70%)",
						}}
						aria-hidden="true"
					/>
				</>
			)}
			<div className={`relative ${isPodcastCover ? `${thumbnailHeight} aspect-square` : "w-full h-full"}`}>
				{item.thumbnail ? (
					<img
						src={item.thumbnail}
						alt={item.title}
						className={`w-full h-full object-cover bg-muted ${isPodcastCover ? "" : "rounded-sm"}`}
					/>
				) : (
					<ThumbnailPlaceholder
					contentType={item.contentType}
					className={isPodcastCover ? "rounded-none bg-muted-foreground/10" : undefined}
				/>
				)}
				{isPodcastWithAudio && (
					<PodcastPlayButton
						track={{
							feedItemId: item._id,
							title: item.title,
							sourceName: item.sourceName,
							thumbnail: item.thumbnail,
							audioUrl: item.audioUrl!,
							duration: item.duration,
						}}
					/>
				)}
				{isYouTubeVideo && (
					<YouTubePlayButton
						track={{
							feedItemId: item._id,
							title: item.title,
							sourceName: item.sourceName,
							thumbnail: item.thumbnail,
							link: item.link,
							duration: item.duration,
						}}
					/>
				)}
				{isArticle && (
					<ArticleReadButton feedItemId={item._id} />
				)}
				{item.playlistVideoPosition != null && (
					<span className="absolute top-0 left-0 inline-flex items-center gap-1 bg-muted/90 text-foreground text-xs font-semibold leading-none px-2 py-1 rounded-br-md rounded-tl-sm backdrop-blur-sm">
						<ListVideo className="size-3.5" />
						{item.playlistVideoPosition}{item.playlistTotalVideos != null ? `/${item.playlistTotalVideos}` : ""}
					</span>
				)}
				{item.duration != null && item.duration > 0 && (
					<span className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] font-medium leading-none px-1 py-0.5 rounded">
						{formatDuration(item.duration)}
					</span>
				)}
			</div>
		</div>
	);

	const sourceClickable = item.sourceId && onSourceClick;

	const metaRow = (
		<div className="flex items-center gap-2 min-w-0 max-w-full">
			{sourceClickable ? (
				<button
					type="button"
					className={`${metaTextSize} text-muted-foreground hover:text-foreground hover:underline cursor-pointer shrink-0 ${transition}`}
					onClick={(e) => { e.stopPropagation(); onSourceClick(item.sourceId!); }}
					onMouseDown={(e) => e.stopPropagation()}
				>
					{item.sourceName}
				</button>
			) : (
				<span className={`${metaTextSize} text-muted-foreground shrink-0 ${transition}`}>
					{item.sourceName}
				</span>
			)}
			<span className={`${metaTextSize} text-muted-foreground shrink-0 ${transition}`}>
				·
			</span>
			<span className={`${metaTextSize} text-muted-foreground shrink-0 ${transition}`}>
				{formatTimeAgo(item.publishedAt)}
			</span>
			{item.forYouReason && (
				<>
					<span className={`${metaTextSize} text-muted-foreground shrink-0`}>·</span>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="inline-flex items-center gap-1 text-[11px] bg-primary/10 text-primary rounded-full px-2 py-0.5 shrink-0 cursor-default">
									<Sparkles className="size-3" />
									{item.forYouReason}
								</span>
							</TooltipTrigger>
							{item.forYouReasonDetail && (
								<TooltipContent>{item.forYouReasonDetail}</TooltipContent>
							)}
						</Tooltip>
					</TooltipProvider>
				</>
			)}
			{noteState.hasNote && (
				<NoteEditPopover
					open={noteState.isEditing}
					onOpenChange={(open) =>
						open ? noteState.startEditing() : noteState.cancelEditing()
					}
					initialNote={noteState.displayNote}
					onSave={noteState.saveNote}
				>
					<button
						type="button"
						className={`flex items-center gap-1 ${metaTextSize} text-muted-foreground italic hover:text-foreground cursor-pointer min-w-0`}
						onClick={(e) => e.stopPropagation()}
						onMouseDown={(e) => e.stopPropagation()}
					>
						<span
							className={`text-xs text-muted-foreground shrink-0 mr-[-2px] ${transition}`}
						>
							·&ensp;
						</span>
						<StickyNote className="size-3 shrink-0" />
						<span className="truncate">{noteState.displayNote}</span>
					</button>
				</NoteEditPopover>
			)}
			{mediaNotesCount != null && mediaNotesCount > 0 && (
				<>
					<span className={`${metaTextSize} text-muted-foreground shrink-0`}>·</span>
					<span className={`flex items-center gap-1 ${metaTextSize} text-muted-foreground shrink-0`}>
						<StickyNote className="size-3" />
						{mediaNotesCount} {mediaNotesCount === 1 ? "note" : "notes"}
					</span>
				</>
			)}
		</div>
	);

	if (isCompact) {
		return (
			<motion.div
				animate={
					isExitingAnimated
						? { x: "100%", opacity: 0, height: 0, marginBottom: 0 }
						: { x: 0, opacity: 1, height: "auto" }
				}
				transition={
					isExitingAnimated
						? {
								x: { duration: 0.25, ease: [0.4, 0, 1, 1] },
								opacity: { duration: 0.1, ease: "easeIn" },
								height: { duration: 0.2, delay: 0.15, ease: [0.22, 1, 0.36, 1] },
								}
						: { duration: 0 }
				}
				className={isExitingAnimated ? "overflow-hidden" : ""}
				onAnimationComplete={() => {
					if (isExitingAnimated) {
						setIsPendingAction(true);
						if (deferredActionRef.current) {
							deferredActionRef.current();
							deferredActionRef.current = null;
						} else {
							onComplete?.();
						}
					}
				}}
			>
				<div
					ref={rowRef}
					data-slot="feed-row"
					tabIndex={0}
					className={`group w-full md:max-w-[896px] flex flex-col px-4 md:px-6 outline-none rounded-sm ${transition} ${isPendingAction ? "hidden" : ""}`}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					onFocus={handleFocus}
					onKeyDown={handleKeyDown}
				>
					<div className="flex items-center gap-2.5 py-3">
						{thumbnail}

						<div className={`flex-1 min-w-0 flex flex-col justify-between ${thumbnailHeight}`}>
							<p
								className={titleClass}
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => onOpen?.()}
							>
								{item.sourceStarred && (
									<Star className="inline size-3 fill-current text-primary mr-1 align-baseline" />
								)}
								{item.title}
							</p>
							{metaRow}
							{item.sourceTags && item.sourceTags.length > 0 && (
								<div className="flex items-center gap-[11px]">
									{item.sourceTags.map((tag) => (
										<button
											key={tag}
											type="button"
											className={`${metaTextSize} text-muted-foreground hover:text-foreground cursor-pointer transition-colors ${transition}`}
											onClick={(e) => {
												e.stopPropagation();
												onTagClick?.(tag);
											}}
											onMouseDown={(e) => e.stopPropagation()}
										>
											#{tag}
										</button>
									))}
								</div>
							)}
							{playlistBadge}
							{topicChips}
						</div>

						<div
							className={`shrink-0 flex items-end gap-2.5 ${thumbnailHeight} ${isHovered ? "opacity-100" : "opacity-0"} ${transition}`}
						>
							{actionButtons}
						</div>
					</div>
					{!hideBorder && <div className="border-b border-border/50" />}
				</div>
			</motion.div>
		);
	}

	return (
		<motion.div
			animate={
				isExitingAnimated
					? { x: "100%", opacity: 0, height: 0, marginBottom: 0 }
					: { x: 0, opacity: 1, height: "auto" }
			}
			transition={
				isExitingAnimated
					? {
							x: { duration: 0.25, ease: [0.4, 0, 1, 1] },
							opacity: { duration: 0.1, ease: "easeIn" },
							height: { duration: 0.2, delay: 0.15, ease: [0.22, 1, 0.36, 1] },
						}
					: { duration: 0 }
			}
			className={isExitingAnimated ? "overflow-hidden" : ""}
			onAnimationComplete={() => {
				if (isExitingAnimated) {
						setIsPendingAction(true);
						if (deferredActionRef.current) {
							deferredActionRef.current();
							deferredActionRef.current = null;
						} else {
							onComplete?.();
						}
					}
			}}
		>
			<div
				ref={rowRef}
				data-slot="feed-row"
				tabIndex={0}
				className={`group w-full md:max-w-[896px] flex flex-col px-4 md:px-6 outline-none rounded-sm ${transition} ${isPendingAction ? "hidden" : ""}`}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onFocus={handleFocus}
				onKeyDown={handleKeyDown}
			>
			<div className="flex items-start gap-3 py-4">
				{thumbnail}

				<div className="relative flex-1 min-w-0 h-[108px]">
					<div className="flex flex-col justify-start gap-1 items-start h-full min-w-0">
						<p
							className={titleClass}
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => onOpen?.()}
						>
							{item.sourceStarred && (
								<Star className="inline size-3 fill-current text-primary mr-1 align-baseline" />
							)}
							{item.title}
						</p>
						{metaRow}
						{item.sourceTags && item.sourceTags.length > 0 && (
							<div className="flex items-center gap-[11px]">
								{item.sourceTags.map((tag) => (
									<button
										key={tag}
										type="button"
										className={`${metaTextSize} text-muted-foreground hover:text-foreground cursor-pointer transition-colors ${transition}`}
										onClick={(e) => {
											e.stopPropagation();
											onTagClick?.(tag);
										}}
										onMouseDown={(e) => e.stopPropagation()}
									>
										#{tag}
									</button>
								))}
							</div>
						)}
						{playlistBadge}
						{topicChips}
					</div>

					<div
						className={`absolute bottom-0 right-0 flex items-center gap-2.5 ${isHovered ? "opacity-100" : "opacity-0"} ${transition}`}
					>
						{actionButtons}
					</div>
				</div>
			</div>
			{!hideBorder && <div className="border-b border-border/50" />}
			</div>
		</motion.div>
	);
});
